import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function Vault({ token }) {
  const toast = useToast();
  const [keys, setKeys] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [auditKeyId, setAuditKeyId] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [form, setForm] = useState({ service_name: 'openai', key_label: '', api_key: '' });
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/vault`, { headers });
      if (res.ok) setKeys((await res.json()).keys || []);
    } catch (e) {}
  };

  useEffect(() => { fetchKeys(); }, [token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/protected/vault`, { method: 'POST', headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to add key'));
      toast('Key stored securely', 'success');
      setShowForm(false);
      setForm({ service_name: 'openai', key_label: '', api_key: '' });
      fetchKeys();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Revoke this key?')) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/vault/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to revoke key'));
      toast('Key revoked', 'success');
      fetchKeys();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const viewAudit = async (keyId) => {
    if (auditKeyId === keyId) { setAuditKeyId(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/protected/vault/${keyId}/audit`, { headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load audit log'));
      setAuditLogs((await res.json()).audit_logs || []);
      setAuditKeyId(keyId);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Key Vault</div>
          <div className="page-subtitle">Encrypted storage for external API keys</div>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add Key'}</button>
      </div>
      <div className="page-body">
        {showForm && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Add API Key</div>
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group">
                  <label>Service</label>
                  <select value={form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })}>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="google">Google</option>
                    <option value="mistral">Mistral</option>
                    <option value="cohere">Cohere</option>
                    <option value="groq">Groq</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Label</label>
                  <input value={form.key_label} onChange={e => setForm({ ...form, key_label: e.target.value })} placeholder="e.g. production" required />
                </div>
              </div>
              <div className="form-group">
                <label>API Key</label>
                <input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="sk-..." required />
              </div>
              <button type="submit" className="btn-primary">Encrypt & Store</button>
            </form>
          </div>
        )}

        <div className="card">
          {keys.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&bull;</div>
              <h3>Vault is empty</h3>
              <p>Store API keys securely. Agents request keys at runtime without them being exposed in code.</p>
              <button className="btn-primary" onClick={() => setShowForm(true)}>Add First Key</button>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Label</th>
                  <th>Key Preview</th>
                  <th>Added</th>
                  <th>Last Accessed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => (
                  <React.Fragment key={key.id}>
                    <tr>
                      <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{key.service_name}</td>
                      <td>{key.key_label}</td>
                      <td><span className="text-mono text-xs">{key.key_preview}</span></td>
                      <td className="text-sm">{new Date(key.created_at).toLocaleDateString()}</td>
                      <td className="text-sm">{key.last_accessed ? new Date(key.last_accessed).toLocaleString() : 'Never'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-ghost btn-sm" onClick={() => viewAudit(key.id)}>
                            {auditKeyId === key.id ? 'Hide' : 'Audit'}
                          </button>
                          <button className="btn-danger btn-sm" onClick={() => handleDelete(key.id)}>Revoke</button>
                        </div>
                      </td>
                    </tr>
                    {auditKeyId === key.id && (
                      <tr>
                        <td colSpan="6" style={{ background: 'var(--bg-primary)', padding: 16 }}>
                          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Access Log</div>
                          {auditLogs.length === 0 ? (
                            <div className="text-sm text-muted">No access recorded yet.</div>
                          ) : (
                            <table>
                              <thead><tr><th>Type</th><th>Agent</th><th>IP</th><th>Time</th></tr></thead>
                              <tbody>
                                {auditLogs.map(log => (
                                  <tr key={log.id}>
                                    <td>{log.access_type}</td>
                                    <td>{log.agent_id || '-'}</td>
                                    <td className="text-mono text-xs">{log.ip_address || '-'}</td>
                                    <td className="text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default Vault;
